import type {
  BrowserNotificationCapabilities,
  NotificationPermissionState,
} from './browser-notification-runtime'
import type { InstallationPlatformHint } from './installation-guidance'

export type WebPushServerState
  = | 'unregistered'
    | 'active'
    | 'locally-disabled'
    | 'temporarily-unhealthy'
    | 'server-invalidated'

export function isActiveWebPushServerState(
  state: WebPushServerState | undefined,
): state is 'active' | 'temporarily-unhealthy' {
  return state === 'active' || state === 'temporarily-unhealthy'
}

export type LocalPushSubscriptionState
  = | 'checking'
    | 'present'
    | 'missing'
    | 'error'

export type WebPushUiState
  = | 'unsupported'
    | 'ios-not-installed'
    | 'permission-default'
    | 'permission-denied'
    | 'permission-granted-unregistered'
    | 'active'
    | 'locally-disabled'
    | 'temporarily-unhealthy'
    | 'server-invalidated'

export interface WebPushStateInput {
  capabilities: BrowserNotificationCapabilities
  platform: InstallationPlatformHint
  permission: NotificationPermissionState
  serverState?: WebPushServerState
  localSubscription: LocalPushSubscriptionState
  serviceAvailable?: boolean
  serverInspectionFailed?: boolean
}

export function resolveWebPushState({
  capabilities,
  platform,
  permission,
  serverState,
  localSubscription,
  serviceAvailable,
  serverInspectionFailed = false,
}: WebPushStateInput): WebPushUiState {
  if (!capabilities.lifecycleLock)
    return 'unsupported'

  if (platform === 'ios' && !capabilities.standalone)
    return 'ios-not-installed'

  if (
    !capabilities.serviceWorker
    || !capabilities.push
    || !capabilities.notification
    || permission === 'unsupported'
  ) {
    return 'unsupported'
  }

  if (permission === 'denied')
    return 'permission-denied'
  if (permission === 'default')
    return 'permission-default'

  if (serverState === 'server-invalidated')
    return 'server-invalidated'
  if (serverState === 'locally-disabled')
    return 'locally-disabled'

  if (
    serverInspectionFailed
    || serviceAvailable === false
    || localSubscription === 'checking'
    || localSubscription === 'error'
  ) {
    return 'temporarily-unhealthy'
  }

  if (serverState === 'temporarily-unhealthy')
    return 'temporarily-unhealthy'

  if (serverState === 'active') {
    return localSubscription === 'present' ? 'active' : 'temporarily-unhealthy'
  }

  return 'permission-granted-unregistered'
}

export function canExplicitlyEnableWebPush(state: WebPushUiState): boolean {
  return (
    state === 'permission-default'
    || state === 'permission-granted-unregistered'
    || state === 'locally-disabled'
    || state === 'server-invalidated'
  )
}

export function canDisableCurrentWebPushDevice(state: WebPushUiState): boolean {
  return state === 'active' || state === 'temporarily-unhealthy'
}
